"use client";

/**
 * WebSocket Subscription System - React Provider
 *
 * This module provides the core WebSocket provider that manages connections and subscriptions
 * for the entire application. It implements a subscription-based system where components
 * must explicitly subscribe to specific alert types to receive messages.
 *
 * Key Features:
 * - Authenticated WebSocket connections with automatic token refresh
 * - Subscription-based message filtering (replaces broadcast-all pattern)
 * - Automatic reconnection with exponential backoff
 * - Health check mechanism for connection stability
 * - Array message handling (backend sends proper arrays, components receive individual messages)
 * - Component-level subscription tracking for debugging
 * - Connection state management and cleanup
 *
 * Architecture:
 * 1. Provider establishes authenticated WebSocket connection
 * 2. Components use useWebSocketSubscription hook to register subscriptions
 * 3. Provider sends subscription messages to backend
 * 4. Backend filters and sends only relevant messages
 * 5. Provider notifies subscribed components of matching messages
 *
 * Breaking Changes:
 * - Removed lastMessage from context (use useWebSocketSubscription instead)
 * - Components without subscriptions receive no messages
 * - Array message handling moved to provider level
 *
 * Performance Improvements:
 * - Only relevant messages trigger component re-renders
 * - Reduced memory usage from message processing
 * - Better scalability with more concurrent users
 *
 * Connection Management:
 * - Automatic reconnection on disconnect
 * - Token refresh on authentication errors
 * - Exponential backoff for failed reconnections
 * - Health checks every 5 minutes
 * - Server-side ready signal before sending subscriptions
 * - Subscription re-establishment on reconnect
 */
import posthog from "posthog-js";
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import useWebSocketClient, { ReadyState } from "react-use-websocket";
import { getAPIBaseURL } from "@/lib/get-base-url";
import { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { useTenant } from "./tenant-provider";
import { useUser } from "./user-provider";
import { invalidateChatUnreadCount } from "@/query/comms/chat";
import { invalidateNotificationList } from "@/query/notifications/notifications";
import { authService } from "@/services/auth-service";
const getWebSocketBaseUrl = () => {
  const url = new URL(getAPIBaseURL());
  const wsProtocol = url.protocol === "https:" ? "wss:" : "ws:";
  return `${wsProtocol}//${url.host}`;
};
type SubscriptionCallback = (message: AlertDetail) => void;

/**
 * WebSocket context interface providing subscription and connection management.
 *
 * This context is consumed by useWebSocketSubscription hooks to register
 * subscriptions and receive filtered messages. Components should not use
 * this context directly - use the hooks instead.
 */
interface WebSocketContextValue {
  /** Send raw message to WebSocket server (for health checks, etc.) */
  sendMessage: (message: string) => void;
  /** Current connection status */
  isConnected: boolean;
  /**
   * Subscribe to specific alert types. Returns unsubscribe function.
   * Used internally by useWebSocketSubscription hook.
   */
  subscribe: (alertTypes: AlertType[], callback: SubscriptionCallback, subscriptionId: string, componentName?: string, providerId?: string | null) => () => void;
}
const WebSocketContext = createContext<WebSocketContextValue | null>(null);

/**
 * Hook to access WebSocket context.
 *
 * This hook provides access to the WebSocket connection and subscription management.
 * It should primarily be used by the useWebSocketSubscription hooks, not directly
 * by components.
 *
 * @returns WebSocket context with sendMessage, isConnected, and subscribe functions
 * @throws Error if used outside of WebSocketProvider
 */
export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error("useWebSocket must be used within a WebSocketProvider");
  }
  return context;
}
const pingInterval = 300000;
const SEEN_IDS_MAX_SIZE = 1000;

/** Random window for the first reconnect attempt; each retry doubles it. */
export const RECONNECT_BASE_MS = 5000;

/**
 * Ceiling on that window — what bounds retry pressure during a long outage.
 * Full jitter draws from [0, window), so the mean interval here is half the
 * cap: lowering this costs twice what it looks like.
 */
export const RECONNECT_CAP_MS = 300_000;

/**
 * Floor between wake-triggered retries. visibilitychange fires on every tab
 * focus, not just a real wake, so without this a user switching tabs during an
 * outage retries as fast as they switch and the ladder stops meaning anything.
 */
export const WAKE_RETRY_MIN_INTERVAL_MS = 5000;

/**
 * WebSocket Provider component that manages the WebSocket connection and subscriptions.
 *
 * This provider should wrap the entire application or the parts that need WebSocket
 * functionality. It handles:
 * - Authenticated WebSocket connection establishment
 * - Subscription management and message routing
 * - Automatic reconnection with exponential backoff
 * - Health checks and connection stability
 * - Token refresh on authentication errors
 *
 * The provider automatically connects when user and tenant are available,
 * and manages the connection lifecycle including cleanup on unmount.
 *
 * @param children - React children that will have access to WebSocket functionality
 */
export function WebSocketProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  const wsBaseUrl = getWebSocketBaseUrl();
  const [socketUrl, setSocketUrl] = useState<string | null>(null);
  const [shouldConnect, setShouldConnect] = useState(false);
  const [subscriptionTrigger, setSubscriptionTrigger] = useState(0);

  // Use refs for timer/retry state to avoid destabilizing the websocketOptions memo.
  // These are only read/written inside onOpen/onClose callbacks, never used for rendering.
  const pingIntervalIdRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef(0);
  const connectionRetryCountRef = useRef(0);
  const retryTimerIdRef = useRef<NodeJS.Timeout | null>(null);
  const connectTimeRef = useRef<number | null>(null);
  const disconnectTimeRef = useRef<number | null>(null);

  // Subscription management - track active components and their alert types
  const activeComponentsRef = useRef<Map<string, {
    alertTypes: AlertType[];
    callback: SubscriptionCallback;
    componentName: string;
    providerId?: string | null;
  }>>(new Map());
  const subscriptionsSentRef = useRef(false);
  const connectionReadyRef = useRef(false);

  // Refs to break circular dependency between useWebSocketClient and callbacks
  const sendMessageRef = useRef<((message: string) => void) | null>(null);
  const readyStateRef = useRef<ReadyState>(ReadyState.UNINSTANTIATED);
  // Last time a wake pre-empted the backoff — see WAKE_RETRY_MIN_INTERVAL_MS.
  const lastRetryNowRef = useRef(0);

  // Track seen notification IDs for deduplication (O(1) lookup)
  // This prevents duplicate notifications from being rendered when:
  // - WebSocket delivers the same message multiple times
  // - Reconnect recovery fetches notifications that WebSocket also delivers
  // - Pub/Sub redelivery causes duplicate messages
  // Do NOT clear on reconnect - keep for session duration
  // Capped at SEEN_IDS_MAX_SIZE entries to prevent unbounded memory growth in long sessions
  const seenNotificationIds = useRef<Set<string>>(new Set());
  useEffect(() => {
    return () => {
      if (pingIntervalIdRef.current) {
        clearInterval(pingIntervalIdRef.current);
      }
      if (retryTimerIdRef.current) {
        clearTimeout(retryTimerIdRef.current);
      }
    };
  }, []);
  const constructSocketUrl = useCallback(async () => {
    const token = await authService.getToken();
    if (token && tenant) {
      return `${wsBaseUrl}/ws?token=${token}&tenant=${tenant.subDomain}`;
    }
    return null;
  }, [tenant, wsBaseUrl]);
  const subscribe = useCallback((alertTypes: AlertType[], callback: SubscriptionCallback, subscriptionId: string, componentName?: string, providerId?: string | null) => {
    const effectiveComponentName = componentName || "UnnamedComponent";

    // Register this subscription with its alert types, callback, component name, and optional provider filter
    activeComponentsRef.current.set(subscriptionId, {
      alertTypes,
      callback,
      componentName: effectiveComponentName,
      providerId
    });

    // Reset subscriptionsSent flag so new subscriptions get sent
    subscriptionsSentRef.current = false;

    // Trigger useEffect to send updated subscriptions
    setSubscriptionTrigger(prev => prev + 1);

    // Return cleanup function
    return () => {
      activeComponentsRef.current.delete(subscriptionId);

      // Reset subscriptions sent flag so remaining components get re-subscribed
      subscriptionsSentRef.current = false;

      // Trigger useEffect to send updated subscriptions (without this component)
      setSubscriptionTrigger(prev_0 => prev_0 + 1);
    };
  }, []);
  const notifySubscribers = useCallback((message: AlertDetail) => {
    // Deduplication check - prevent duplicate notifications from being processed
    if (seenNotificationIds.current.has(message.id)) {
      console.debug(`Duplicate notification filtered: ${message.id}`);
      return;
    }
    // Evict oldest entries when the set exceeds the cap
    if (seenNotificationIds.current.size >= SEEN_IDS_MAX_SIZE) {
      const idsToDelete = Array.from(seenNotificationIds.current).slice(0, seenNotificationIds.current.size - SEEN_IDS_MAX_SIZE + 1);
      idsToDelete.forEach(id => seenNotificationIds.current.delete(id));
    }
    seenNotificationIds.current.add(message.id);
    let subscribersNotified = 0;
    const notifiedComponents: string[] = [];
    activeComponentsRef.current.forEach(({
      alertTypes: alertTypes_0,
      callback: callback_0,
      componentName: componentName_0
    }, _subscriptionId) => {
      if (alertTypes_0.includes(message.type)) {
        callback_0(message);
        subscribersNotified++;
        notifiedComponents.push(componentName_0);
      }
    });
    if (subscribersNotified > 0) {
      console.debug(`WebSocket received ${message.type} → notified [${notifiedComponents.join(", ")}]`);
    }
  }, []);
  const attemptReconnect = useCallback(async (currentRetryCount: number) => {
    console.debug(`Attempting to reconnect (Attempt: ${currentRetryCount + 1})...`);
    await authService.refreshAuthToken();
    const newSocketUrl = await constructSocketUrl();
    if (newSocketUrl) {
      setSocketUrl(newSocketUrl);
    } else {
      console.debug("Could not construct socket URL for reconnect attempt.");
    }
  }, [constructSocketUrl]);

  // Full jitter — random(0, min(cap, base * 2^attempt)). Disconnects arrive as a
  // herd (pod recycles, rollouts); the old schedule fired attempts 1 and 2 at
  // 0ms with no jitter, so every client returned in the same instant.
  // Multiplying by random() spreads them across the window rather than
  // clustering at its centre, unlike the +/-10% jitter in
  // transcription-queue/processor.ts. Attempt 1 is delayed for the same reason —
  // a zero-delay fast path would undo this.
  // Brooker, "Exponential Backoff And Jitter", AWS, 2015.
  const scheduleRetry = useCallback(() => {
    if (retryTimerIdRef.current) {
      clearTimeout(retryTimerIdRef.current);
    }
    const currentRetryCount_0 = retryCountRef.current;
    retryCountRef.current = currentRetryCount_0 + 1;
    const delay = Math.random() * Math.min(RECONNECT_CAP_MS, RECONNECT_BASE_MS * 2 ** currentRetryCount_0);
    retryTimerIdRef.current = setTimeout(() => {
      // Clear before awaiting so the ref means "retry scheduled", not "retry
      // scheduled or running" — retryNow below relies on that distinction.
      retryTimerIdRef.current = null;
      void attemptReconnect(currentRetryCount_0);
    }, delay);
  }, [attemptReconnect]);

  // Collapse a pending backoff and go now. Paired with scheduleRetry so both
  // transitions of the retry timer live in one place: callers decide *whether*
  // to retry early, never how the timer moves.
  //
  // Keeps retryCountRef untouched. Resetting it would let a flapping network
  // defeat the backoff, retrying at full speed on every flap; onOpen already
  // resets it, so a genuine reconnect clears it anyway.
  const retryNow = useCallback(() => {
    // No pending timer means there is no wait to collapse: either nothing is
    // scheduled, or an attempt is already running — scheduleRetry's timer nulls
    // this before invoking it, so the same check keeps a wake from starting a
    // competing attempt.
    if (!retryTimerIdRef.current) return;

    // Pre-empting the ladder has to stay rare, or it replaces it. Every tab
    // focus fires visibilitychange, so an unbounded version lets someone
    // flipping between tabs during an outage retry at the speed they flip.
    const now = Date.now();
    if (now - lastRetryNowRef.current < WAKE_RETRY_MIN_INTERVAL_MS) return;
    lastRetryNowRef.current = now;
    clearTimeout(retryTimerIdRef.current);
    retryTimerIdRef.current = null;
    void attemptReconnect(retryCountRef.current);
  }, [attemptReconnect]);
  const sendMessageWithTokenCheck = useCallback(async (message_0: string) => {
    const token_0 = await authService.getToken();
    if (!token_0) {
      await authService.refreshAuthToken();
      const newSocketUrl_0 = await constructSocketUrl();
      setSocketUrl(newSocketUrl_0);
    }
    if (readyStateRef.current === ReadyState.OPEN && sendMessageRef.current && socketUrl) {
      sendMessageRef.current(message_0);
    }
  }, [socketUrl, constructSocketUrl]);
  useEffect(() => {
    const updateSocketUrl = async () => {
      const newSocketUrl_1 = await constructSocketUrl();
      if (newSocketUrl_1) {
        setSocketUrl(newSocketUrl_1);
      } else {
        setSocketUrl(null);
      }
    };
    updateSocketUrl();
  }, [currentUser, tenant, constructSocketUrl]);
  useEffect(() => {
    const timer = setTimeout(() => setShouldConnect(true), 1000);
    return () => clearTimeout(timer);
  }, []);
  const sendPendingSubscriptions = useCallback(() => {
    if (!sendMessageRef.current || subscriptionsSentRef.current) return;

    // Group subscriptions by provider_id
    const subscriptionsByProvider = new Map<string | null | undefined, {
      alertTypes: Set<AlertType>;
      components: string[];
    }>();
    activeComponentsRef.current.forEach(({
      alertTypes: alertTypes_1,
      componentName: componentName_1,
      providerId: providerId_0
    }, _subscriptionId_0) => {
      if (!subscriptionsByProvider.has(providerId_0)) {
        subscriptionsByProvider.set(providerId_0, {
          alertTypes: new Set(),
          components: []
        });
      }
      const group = subscriptionsByProvider.get(providerId_0)!;
      alertTypes_1.forEach(type => group.alertTypes.add(type));
      group.components.push(componentName_1);
    });

    // Send separate subscription messages for each provider_id group
    subscriptionsByProvider.forEach((group_0, providerId_1) => {
      if (group_0.alertTypes.size > 0) {
        const subscriptionMessage: {
          type: string;
          alertTypes: AlertType[];
          providerId?: string;
        } = {
          type: "subscribe",
          alertTypes: Array.from(group_0.alertTypes)
        };
        if (providerId_1) {
          subscriptionMessage.providerId = providerId_1;
        }
        sendMessageRef.current!(JSON.stringify(subscriptionMessage));
        console.debug(`WebSocket subscribing to ${group_0.alertTypes.size} alert types${providerId_1 ? ` for provider ${providerId_1}` : " (no provider filter)"}:`, Array.from(group_0.alertTypes));
        group_0.components.forEach(componentName_2 => {
          console.debug(`  └─ [${componentName_2}]`);
        });
      }
    });
    subscriptionsSentRef.current = true;
  }, []);
  const websocketOptions = useMemo(() => ({
    share: false,
    shouldReconnect: () => {
      console.debug("shouldReconnect called, returning false for manual handling.");
      return false;
    },
    retryOnError: false,
    onOpen: () => {
      const activeComponents = Array.from(activeComponentsRef.current.values()).map(({
        componentName: componentName_3
      }) => componentName_3);
      const componentsList = activeComponents.length > 0 ? `[${activeComponents.join(", ")}]` : "[WebSocket]";
      console.debug(`WebSocket connected ${componentsList}`);

      // Capture timestamp immediately so onOpen work doesn't inflate downtime.
      const reconnectNow = performance.now();

      // Track reconnect — only if a prior connection was successfully established.
      // connectTimeRef is set in onOpen, so null means no prior successful connection.
      if (retryCountRef.current > 0 && disconnectTimeRef.current !== null && connectTimeRef.current !== null) {
        try {
          const downtimeMs = reconnectNow - disconnectTimeRef.current;
          posthog.capture("ws_reconnected", {
            downtime_ms: downtimeMs,
            retry_count: retryCountRef.current
          });
        } catch (e) {
          console.error("Failed to capture ws_reconnected event", e);
        }
      }
      connectionRetryCountRef.current = retryCountRef.current;
      connectTimeRef.current = reconnectNow;
      disconnectTimeRef.current = null;

      // Reset subscription and connection ready state for new connection
      subscriptionsSentRef.current = false;
      connectionReadyRef.current = false;
      retryCountRef.current = 0;
      if (retryTimerIdRef.current) {
        clearTimeout(retryTimerIdRef.current);
        retryTimerIdRef.current = null;
      }
      if (pingIntervalIdRef.current) {
        clearInterval(pingIntervalIdRef.current);
      }
      pingIntervalIdRef.current = setInterval(() => {
        sendMessageWithTokenCheck("health-check");
      }, pingInterval);

      // Refresh the notification list/badge so the panel shows any missed notifications.
      // We intentionally do NOT toast recovered notifications — only live WebSocket
      // messages produce toasts. Missed notifications appear in the panel via this refetch.
      invalidateNotificationList();

      // Resync the chat unread-count badge. While disconnected the client may
      // have missed CHAT_UPDATE count pushes; refetch once on (re)connect so
      // the badge can't stay stale until the next live status flip corrects it.
      invalidateChatUnreadCount();
    },
    onClose: async (event: CloseEvent) => {
      const activeComponents_0 = Array.from(activeComponentsRef.current.values()).map(({
        componentName: componentName_4
      }) => componentName_4);
      const componentsList_0 = activeComponents_0.length > 0 ? `[${activeComponents_0.join(", ")}]` : "[WebSocket]";
      console.debug(`WebSocket disconnected ${componentsList_0}`, {
        code: event.code,
        reason: event.reason,
        wasClean: event.wasClean,
        timestamp: new Date().toISOString()
      });
      const now_0 = performance.now();
      // Only record the first disconnect timestamp — failed reconnect
      // attempts also fire onClose but should not overwrite the original
      // disconnect time (which would under-report downtime in ws_reconnected).
      const isInitialDisconnect = disconnectTimeRef.current === null;
      disconnectTimeRef.current = disconnectTimeRef.current ?? now_0;

      // Only emit ws_disconnected for the initial drop from an established
      // connection — skip failed reconnect attempts (which would inflate
      // event counts) and never-established connections (first-connect failures).
      if (isInitialDisconnect && connectTimeRef.current !== null) {
        try {
          posthog.capture("ws_disconnected", {
            close_code: event.code,
            close_reason: (event.reason ?? "").slice(0, 200) || null,
            was_clean: event.wasClean,
            connection_duration_ms: now_0 - connectTimeRef.current,
            retry_count: connectionRetryCountRef.current,
            navigator_online: navigator.onLine,
            document_visibility: document.visibilityState
          });
        } catch (e_0) {
          console.error("Failed to capture ws_disconnected event", e_0);
        }
      }

      // Reset subscription and connection ready state on disconnect
      subscriptionsSentRef.current = false;
      connectionReadyRef.current = false;
      if (pingIntervalIdRef.current) {
        clearInterval(pingIntervalIdRef.current);
        pingIntervalIdRef.current = null;
      }
      scheduleRetry();
    },
    onError: (event_0: Event) => console.error("WebSocket encountered an error", event_0),
    onMessage: async (event_1: MessageEvent) => {
      try {
        let jsonData: string;
        if (event_1.data instanceof Blob) {
          jsonData = await event_1.data.text();
        } else {
          jsonData = event_1.data;
        }
        const parsedData = JSON.parse(jsonData);

        // Handle connection ready signal
        if (parsedData.type === "connection_ready") {
          connectionReadyRef.current = true;
          // Send pending subscriptions now that server is ready
          if (!subscriptionsSentRef.current && activeComponentsRef.current.size > 0) {
            sendPendingSubscriptions();
          }
          return;
        }

        // Handle subscription confirmations
        if (parsedData.type === "subscription_confirmed") {
          return; // Don't process subscription confirmations as alerts
        }

        // Handle health check responses
        if (parsedData.response === "healthy") {
          return; // Don't process health check responses as alerts
        }

        // parsedData is now correctly structured (array of objects or single object)
        const data = parsedData;

        // Log all incoming messages and notify subscribers
        if (Array.isArray(data)) {
          data.forEach(message_1 => notifySubscribers(message_1));
        } else {
          notifySubscribers(data);
        }
      } catch (error) {
        console.error("Error parsing WebSocket message:", error);
      }
    }
  }), [scheduleRetry, notifySubscribers, sendMessageWithTokenCheck, sendPendingSubscriptions]);
  const {
    sendMessage,
    readyState
  } = useWebSocketClient(shouldConnect ? socketUrl : null, websocketOptions);

  // Sync refs in useEffect so they are only written during commit phase,
  // which is safe under React 18 concurrent/strict mode.
  useEffect(() => {
    sendMessageRef.current = sendMessage;
    readyStateRef.current = readyState;
  });

  // Jitter only pays off for correlated disconnects (rollouts, pod recycles).
  // A wake from sleep or a wifi flap hits one client, so there is no herd to
  // spread and the wait is pure latency — a client deep in the ladder would
  // otherwise sit idle for up to RECONNECT_CAP_MS with the network back.
  useEffect(() => {
    const onWake = () => {
      if (document.visibilityState !== "visible" || !navigator.onLine) return;
      if (readyStateRef.current === ReadyState.OPEN || readyStateRef.current === ReadyState.CONNECTING) {
        return;
      }
      retryNow();
    };
    window.addEventListener("online", onWake);
    document.addEventListener("visibilitychange", onWake);
    return () => {
      window.removeEventListener("online", onWake);
      document.removeEventListener("visibilitychange", onWake);
    };
  }, [retryNow]);

  // Send subscriptions when WebSocket becomes ready and server signals ready
  useEffect(() => {
    if (readyState === ReadyState.OPEN && connectionReadyRef.current && !subscriptionsSentRef.current && activeComponentsRef.current.size > 0) {
      sendPendingSubscriptions();
    }
  }, [readyState, sendPendingSubscriptions, subscriptionTrigger]);
  const value = useMemo(() => ({
    sendMessage: sendMessageWithTokenCheck,
    isConnected: readyState === ReadyState.OPEN,
    subscribe
  }), [readyState, sendMessageWithTokenCheck, subscribe]);
  return <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>;
}